import { useCallback, useMemo, useState } from 'react'
import {
  CheckCircleTwoTone,
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons'
import { Button, Divider, message, Popconfirm, Table } from 'antd'
import { ListError, PageHeader } from '../Common'
import { useAsync, useIsMobile } from '../../hooks'
import api from '../../api'
import Filters from '../Forms/Filters'
import AddNewItemButton from '../Common/AddNewItemButton'
import { toPath } from '../../routes'
import DeviceModelMobileCard from './DeviceModelMobileRow'
import ButtonLink from '../Common/ButtonLink'

const COLUMNS = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Manufacturer',
    dataIndex: 'manufacturer',
    key: 'manufacturer',
  },
  {
    title: 'ICM Capable',
    dataIndex: 'icmCapable',
    key: 'icmCapable',
    align: 'center',
    render: value =>
      value ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : null,
  },
]

function filter(value, search) {
  return value.toLowerCase().includes(search.toLowerCase())
}

export default function DeviceModels() {
  const isMobile = useIsMobile()
  const fetchDeviceModels = useCallback(() => api.deviceModels.list(), [])
  const {
    pending,
    error,
    clearError,
    value: deviceModels,
    execute: fetchList,
  } = useAsync(fetchDeviceModels)
  const [queryFilter, setQueryFilter] = useState('')
  const onSubmit = useCallback(({ query }) => {
    setQueryFilter(query)
  }, [])

  const models = useMemo(
    () =>
      (deviceModels || []).filter(
        ({ name, manufacturer }) =>
          filter(name, queryFilter) || filter(manufacturer, queryFilter),
      ),
    [deviceModels, queryFilter],
  )

  const { pending: deleting, execute: handleDelete } = useAsync(async id => {
    try {
      await api.deviceModels.delete(id)
      message.success('Device model successfully deactivated')
    } catch (e) {
      message.error(
        `There was an error deactivating this device model: ${e.message}`,
      )
    }
    fetchList()
  }, false)

  const columns = useMemo(
    () => [
      ...COLUMNS,
      {
        title: 'Actions',
        key: 'actions',
        render: (text, record) => (
          <span>
            <ButtonLink
              to={toPath.deviceModels.deviceModel.edit({ id: record.id })}
              className="edit-button"
              type="link"
              icon={<EditOutlined />}
              aria-label="Edit device model"
            />
            <Divider type="vertical" />
            <Popconfirm
              title="Are you sure you want to deactivate this device model?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
              placement="topRight"
            >
              <Button
                type="link"
                icon={<DeleteOutlined />}
                className="delete-button"
                aria-label="Deactivate device model"
              />
            </Popconfirm>
          </span>
        ),
      },
    ],
    [handleDelete],
  )

  // On mobile the whole row collapses into a single card; no expand toggle is
  // needed because every desktop column is shown on the card.
  const mobileColumns = useMemo(
    () => [
      {
        title: 'Device model',
        key: 'deviceModel',
        render(_, deviceModel) {
          return (
            <DeviceModelMobileCard
              deviceModel={deviceModel}
              onDelete={handleDelete}
            />
          )
        },
      },
    ],
    [handleDelete],
  )

  return (
    <div>
      <PageHeader
        title="Device Models"
        extra={
          <AddNewItemButton
            itemName="device model"
            path={toPath.deviceModels.new()}
            icon={<PlusCircleOutlined />}
          />
        }
      />
      {error && <ListError description={error.message} onClose={clearError} />}
      <Filters onSubmit={onSubmit} showSearch />
      <Table
        size={isMobile ? 'small' : undefined}
        columns={isMobile ? mobileColumns : columns}
        showHeader={!isMobile}
        dataSource={models}
        loading={pending || deleting}
        rowKey="id"
      />
    </div>
  )
}
